import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {map} from 'rxjs/operators';
import {
    AttachmentFile,
    fileAttachmentListSchema,
    fileAttachmentSchema,
} from 'src/app/reporting/costs/models/file-attachment.model';
import {FileAttachmentRequest} from 'src/app/reporting/costs/models/file-attachment-request.model';
import {
    ReportingAccount,
    ReportingBuildingPeriod,
} from '@reporting/overview/account-overview.model';

export type ReportingAccountWithAttachments = ReportingAccount & {
    attachments: AttachmentFile[];
    uploading: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class ReportConsumersUploadService {
    private baseUrl = `${environment.selfServiceUrl}`;
    private activeAccountsWithAttachments = new BehaviorSubject<ReportingAccountWithAttachments[]>(
        [],
    );

    constructor(private http: HttpClient) {}

    fetchAccountAttachments(
        building: ReportingBuildingPeriod,
    ): Observable<ReportingAccountWithAttachments[]> {
        forkJoin(
            building.accounts.map(account =>
                this.getConsumerDocAttachments(
                    building.buildingNo,
                    building.periodId,
                    account.accountPeriodId,
                ).pipe(
                    map(attachments => ({
                        ...account,
                        attachments: attachments,
                        uploading: false,
                    })),
                ),
            ),
        ).subscribe(accounts => this.activeAccountsWithAttachments.next(accounts));
        return this.activeAccountsWithAttachments;
    }

    private getConsumerDocAttachments(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
    ): Observable<AttachmentFile[]> {
        const url = `${this.baseUrl}/buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/consumers/attachments`;
        return this.http
            .get(url, {withCredentials: true})
            .pipe(map(data => fileAttachmentListSchema.parse(data)));
    }

    createConsumerDocAttachment(
        buildingNo: string,
        periodId: string,
        accountPeriodId: string,
        fileAttachment: FileAttachmentRequest,
    ): Observable<AttachmentFile> {
        this.markAsUploading(accountPeriodId);
        const url = `${this.baseUrl}/buildings/${buildingNo}/periods/${periodId}/accountperiods/${accountPeriodId}/consumers/attachments`;
        return this.http
            .post(url, fileAttachment, {
                withCredentials: true,
            })
            .pipe(
                map(item => fileAttachmentSchema.parse(item)),
                tap(attachment => {
                    this.addAttachmentToAccount(accountPeriodId, attachment);
                }),
            );
    }

    private markAsUploading(accountPeriodId: string): void {
        const accounts = this.activeAccountsWithAttachments.value;
        const account = accounts.find(a => a.accountPeriodId === accountPeriodId);
        if (account != null) {
            account.uploading = true;
        }
        this.activeAccountsWithAttachments.next(accounts);
    }

    private addAttachmentToAccount(accountPeriodId: string, item: AttachmentFile): void {
        const accounts = this.activeAccountsWithAttachments.value;
        const account = accounts.find(a => a.accountPeriodId === accountPeriodId);
        if (account != null) {
            account.attachments.push(item);
            account.uploading = false;
        }
        this.activeAccountsWithAttachments.next(accounts);
    }

    deleteConsumerDocAttachments(
        buildingNo: string,
        period: string,
        accountPeriodId: string,
        attachmentId: string,
    ): Observable<unknown> {
        const accounts = this.activeAccountsWithAttachments.value;
        const account = accounts.find(a => a.accountPeriodId === accountPeriodId);
        if (account != null) {
            account.attachments = account.attachments.filter(a => a.attachmentId !== attachmentId);
        }
        this.activeAccountsWithAttachments.next(accounts);

        const url = `${this.baseUrl}/buildings/${buildingNo}/periods/${period}/accountperiods/${accountPeriodId}/consumers/attachments/${attachmentId}`;
        return this.http.delete(url, {withCredentials: true});
    }

    beginConsumerReporting(buildingNo: string, periodId: string): Observable<unknown> {
        const url = `${this.baseUrl}/buildings/${buildingNo}/periods/${periodId}/beginConsumersReporting`;
        return this.http.post(url, 'Let me start :)', {withCredentials: true});
    }
}
